import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '@biletix/env';
import { SearchResultItem, SearchAggregationItem, SearchFilters } from '@biletix/core/models/search/search-result.model';
import { SearchSuggestionsData, SuggestionItem } from '@biletix/core/models/search/search-suggestion.model';

interface SolrSelectResponse {
  response: {
    numFound: number;
    start: number;
    docs: Record<string, unknown>[];
  };
  facet_counts?: {
    facet_fields: Record<string, (string | number)[]>;
  };
}

interface SolrSuggestResponse {
  facet_counts?: {
    facet_fields: {
      GroupSuggestion?: (string | number)[];
      EventSuggestion?: (string | number)[];
      VenueSuggestion?: (string | number)[];
      CitySuggestion?: (string | number)[];
    };
  };
}

@Injectable({ providedIn: 'root' })
export class SolrService {
  private readonly base = environment.solr.baseUrl;
  private readonly formHeaders = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

  constructor(private http: HttpClient) {}

  suggestions(lang: string, searchText: string): Observable<SearchSuggestionsData> {
    const url = `${this.base}/${lang}/select?qt=autocomplete`;
    const escaped = this.escapeSolrSpecialChars(searchText.trim());
    const q = `SearchSuggest:${this.appendWildcardToLastTerm(escaped)}`;

    const now = new Date();
    const pad = (n: number): string => String(n).padStart(2, '0');
    const isoDate = (d: Date): string =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}Z`;
    const pastDate = isoDate(new Date(now.getTime() - 2 * 365 * 24 * 60 * 60 * 1000));
    const futureDate = isoDate(new Date(now.getTime() + 4 * 365 * 24 * 60 * 60 * 1000));

    const params = new HttpParams()
      .set('wt', 'json')
      .set('rows', '0')
      .set('facet', 'true')
      .append('facet.field', 'GroupSuggestion')
      .append('facet.field', 'EventSuggestion')
      .append('facet.field', 'VenueSuggestion')
      .append('facet.field', 'CitySuggestion')
      .set('facet.mincount', '1')
      .set('facet.limit', '20')
      .set('q', q)
      .set('fq', `end:[${pastDate} TO ${futureDate}]`);

    return this.http
      .post<SolrSuggestResponse>(url, params.toString(), { headers: this.formHeaders })
      .pipe(map((res) => this.parseSuggestions(res)));
  }

  private parseSuggestions(res: SolrSuggestResponse): SearchSuggestionsData {
    const fields = res.facet_counts?.facet_fields ?? {};
    return {
      suggestions: {
        groupName: this.parseFacetField(fields['GroupSuggestion']),
        name: [
          ...this.parseFacetField(fields['EventSuggestion']),
          ...this.parseFacetField(fields['CitySuggestion']),
        ],
        venue: this.parseFacetField(fields['VenueSuggestion']),
      },
    };
  }

  private parseFacetField(field: (string | number)[] | undefined): SuggestionItem[] {
    if (!field?.length) return [];
    const items: SuggestionItem[] = [];
    for (let i = 0; i + 1 < field.length; i += 2) {
      const label = String(field[i]);
      const count = Number(field[i + 1]);
      items.push({ key: label, label, count });
    }
    return items;
  }

  private escapeSolrSpecialChars(input: string): string {
    return input.replace(/[+\-!(){}[\]^"~*?:\\/]/g, '\\$&').replace(/\s+/g, ' ').trim();
  }

  private appendWildcardToLastTerm(escaped: string): string {
    const lastSpace = escaped.lastIndexOf(' ');
    if (lastSpace < 0) return `${escaped}*`;
    return `${escaped.substring(0, lastSpace + 1)}${escaped.substring(lastSpace + 1)}*`;
  }

  search(
    lang: string,
    filters: SearchFilters,
    from = 0,
    pageSize = 30
  ): Observable<SearchResultItem[]> {
    const url = `${this.base}/${lang}/select`;
    const params = this.buildSearchParams(filters, from, pageSize);
    return this.http
      .post<SolrSelectResponse>(url, params.toString(), { headers: this.formHeaders })
      .pipe(map((res) => res.response.docs.map((d) => this.toResultItem(d, lang))));
  }

  searchWithFacets(
    lang: string,
    filters: SearchFilters
  ): Observable<{ items: SearchResultItem[]; aggregations: Record<string, SearchAggregationItem[]> }> {
    const url = `${this.base}/${lang}/select`;
    const params = this.buildSearchParams(filters, 0, 2500)
      .set('facet', 'true')
      .set('facet.mincount', '1')
      .append('facet.field', 'category')
      .append('facet.field', 'venuecode')
      .append('facet.field', 'region')
      .append('facet.field', 'subcategory');
      // .append('spellcheck', 'true')
      // .append('spellcheck.dictionary', 'default')
      // .append('spellcheck.count', '10')
      // .append('spellcheck.extendedResults', 'true')
      // .append('spellcheck.collate', 'true')
      // .append('spellcheck.q', filters.searchtext?.trim() || '*');
    return this.http.post<SolrSelectResponse>(url, params.toString(), { headers: this.formHeaders }).pipe(
      map((res) => {
        const docs = res.response.docs;
        return {
          items: docs.map((d) => this.toResultItem(d, lang)),
          aggregations: this.toAggregations(res.facet_counts?.facet_fields ?? {}, docs, filters.searchtext),
        };
      })
    );
  }

  private buildSearchParams(filters: SearchFilters, from: number, rows: number): HttpParams {
    const q = this.buildQueryString(filters.searchtext);
    let params = new HttpParams()
      .set('wt', 'json')
      .set('q', q)
      .set('sort', 'score desc,start asc')
      .set('start', from.toString())
      .set('rows', rows.toString());

    const fqs = this.buildFilterQueries(filters);
    for (const fq of fqs) {
      params = params.append('fq', fq);
    }
    return params;
  }

  private buildQueryString(searchtext: string): string {
    const t = searchtext?.trim();
    if (!t || t === '*') return '*:*';
    const esc = this.escape(t);
    if (!esc) return '*:*';
    return /\s/.test(esc)? `(name:"${esc}" OR artist:"${esc}" OR venue:"${esc}" OR city:"${esc}" OR description:"${esc}" OR pdfinfo:"${esc}")`:
      `(name:${esc}* OR artist:${esc}* OR venue:${esc}* OR city:${esc}* OR description:${esc}* OR pdfinfo:${esc}*)`;
  }

  private buildFilterQueries(filters: SearchFilters): string[] {
    const fqs: string[] = [];

    if (filters.maincategory?.length) {
      fqs.push(this.multiValueFq('category', filters.maincategory));
    } else if (filters.category?.length) {
      fqs.push(this.multiValueFq('category', filters.category));
    }
    if (filters.city?.length) {
      fqs.push(this.multiValueFq('city', filters.city));
    }
    if (filters.venue?.length) {
      fqs.push(this.multiValueFq('venuecode', filters.venue));
    }
    if (filters.subcategory?.length) {
      fqs.push(this.multiValueFq('subcategory', filters.subcategory));
    }

    if (filters.gt && filters.lt) {
      fqs.push(`start:[${filters.gt}T00:00:00Z TO ${filters.lt}T23:59:59Z]`);
    } else if (filters.date?.length) {
      const dateRange = this.dateLabelToRange(filters.date[0]);
      if (dateRange) {
        fqs.push(`start:[${dateRange.gt}T00:00:00Z TO ${dateRange.lt}T23:59:59Z]`);
      }
    } else {
      const today = new Date().toISOString().split('T')[0];
      const twoYearsLater = new Date(Date.now() + 2 * 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      fqs.push(`start:[${today}T00:00:00Z TO ${twoYearsLater}T23:59:59Z]`);
    }
    return fqs;
  }

  private dateLabelToRange(label: string): { gt: string; lt: string } | null {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    const day = now.getDay(); // 0=Pazar
    switch (label) {
      case 'today': {
        const t = iso(now);
        return { gt: t, lt: t };
      }
      case 'weekend': {
        const daysUntilSat = (6 - day + 7) % 7;
        const sat = new Date(now); sat.setDate(now.getDate() + daysUntilSat);
        const sun = new Date(sat); sun.setDate(sat.getDate() + 1);
        return { gt: iso(sat), lt: iso(sun) };
      }
      case 'nextweekend': {
        const daysUntilSat = (6 - day + 7) % 7 + 7;
        const sat = new Date(now); sat.setDate(now.getDate() + daysUntilSat);
        const sun = new Date(sat); sun.setDate(sat.getDate() + 1);
        return { gt: iso(sat), lt: iso(sun) };
      }
      case 'thisweek': {
        const end = new Date(now); end.setDate(now.getDate() + (7 - day));
        return { gt: iso(now), lt: iso(end) };
      }
      case 'next14days': {
        const end = new Date(now); end.setDate(now.getDate() + 13);
        return { gt: iso(now), lt: iso(end) };
      }
      case 'next30days': {
        const end = new Date(now); end.setDate(now.getDate() + 29);
        return { gt: iso(now), lt: iso(end) };
      }
      default:
        return null;
    }
  }

  private multiValueFq(field: string, values: string[]): string {
    const parts = values.map((v) => `"${this.escape(v)}"`).join(' OR ');
    return `${field}:(${parts})`;
  }

  private toResultItem(doc: Record<string, unknown>, lang: string): SearchResultItem {
    const type = this.str(doc['type']);
    const id = this.str(doc['id']);
    const name = this.firstStr(doc['name']);
    const imageUrl = this.str(doc['image_url']);
    return {
      id,
      groupId: type === 'group' ? id : '',
      name,
      groupName: type === 'group' ? name : '',
      shortName: '',
      groupShortName: '',
      category: this.str(doc['category']),
      subCategory: this.firstStr(doc['subcategory']),
      dataType: type,
      lang,
      image: imageUrl,
      groupImage: imageUrl,
      status: this.str(doc['status']),
      venueCode: this.str(doc['venuecode']),
      liveEvent: doc['liveevent'] === true || doc['liveevent'] === 'true',
      eventDescription: this.firstStr(doc['description']),
      firstActivePerfDate: this.toIsoDate(doc['start']),
      start: this.toIsoDate(doc['start']),
      lastActivePerfDate: this.toIsoDate(doc['end']),
      end: this.toIsoDate(doc['end']),
      cityNames: this.strArray(doc['city']),
      venueNames: this.strArray(doc['venue']),
      venueShortNames: this.strArray(doc['svenue'] ?? doc['venue']),
      regionNames: this.strArray(doc['region']),
      perfDates: [this.toIsoDate(doc['start'])].filter(Boolean),
    };
  }

  private toAggregations(
    facetFields: Record<string, (string | number)[]>,
    docs: Record<string, unknown>[],
    searchtext?: string
  ): Record<string, SearchAggregationItem[]> {
    const result: Record<string, SearchAggregationItem[]> = {};

    // region: facet.field
    const rawRegion = facetFields['region'];
    if (rawRegion?.length) {
      const items: SearchAggregationItem[] = [];
      for (let i = 0; i + 1 < rawRegion.length; i += 2) {
        const key = String(rawRegion[i]);
        const count = Number(rawRegion[i + 1]);
        if (count > 0) items.push({ key, label: key, count, category: 'region' });
      }
      result['region'] = items;
    }

    // city/venue için: arama metni bir şehir/mekan adıyla eşleşen doc'ları öncelikli kullan.
    // Böylece "diyarbakır" aramasında sadece Diyarbakır'daki mekanlar/şehirler gösterilir;
    // açıklama metni içinde diyarbakır geçen İstanbul/Urfa vb. doc'lar filtre listesine girmez.
    const q = searchtext?.trim().toLocaleLowerCase('tr') ?? '';
    const localDocs = q
      ? docs.filter((doc) => {
          const cities = this.strArray(doc['city']).map((c) => c.toLocaleLowerCase('tr'));
          const venue = this.str(doc['svenue']).toLocaleLowerCase('tr');
          return cities.some((c) => c.includes(q)) || venue.includes(q);
        })
      : docs;
    // Eğer hiç local doc yoksa (arama sanatçı adı gibi) tüm doc'lara geri dön
    const aggDocs = localDocs.length > 0 ? localDocs : docs;

    const venueCodeToName = new Map<string, string>();
    const venueCodeCount = new Map<string, number>();
    const cityCountMap = new Map<string, number>();
    const subToCat = new Map<string, string>();

    for (const doc of aggDocs) {
      const code = this.str(doc['venuecode']);
      const name = this.str(doc['svenue']);
      if (code && name) {
        venueCodeToName.set(code, name);
        venueCodeCount.set(code, (venueCodeCount.get(code) ?? 0) + 1);
      }
      for (const city of this.strArray(doc['city'])) {
        cityCountMap.set(city, (cityCountMap.get(city) ?? 0) + 1);
      }
    }

    // subcategory category eşlemesi için tüm doc'ları kullan
    for (const doc of docs) {
      const cat = this.str(doc['category']);
      const sub = this.firstStr(doc['subcategory']);
      if (sub && cat && !subToCat.has(sub)) subToCat.set(sub, cat);
    }

    // venue: aggDocs'tan hesaplanan sayımlar
    const venueItems: SearchAggregationItem[] = [];
    venueCodeCount.forEach((count, code) => {
      const label = venueCodeToName.get(code) ?? code;
      venueItems.push({ key: code, label, count, category: 'venue' });
    });
    venueItems.sort((a, b) => b.count - a.count);
    if (venueItems.length) result['venue'] = venueItems;

    // city: aggDocs'tan hesaplanan sayımlar
    const cityItems: SearchAggregationItem[] = [];
    cityCountMap.forEach((count, cityName) => {
      cityItems.push({ key: cityName, label: cityName, count, category: 'city' });
    });
    cityItems.sort((a, b) => b.count - a.count);
    if (cityItems.length) result['city'] = cityItems;

    // subcategory: facet.field sayımları, category bilgisi docs'tan
    const rawSubcategory = facetFields['subcategory'];
    if (rawSubcategory?.length) {
      const categoryItems: SearchAggregationItem[] = [];
      for (let i = 0; i + 1 < rawSubcategory.length; i += 2) {
        const val = String(rawSubcategory[i]);
        const count = Number(rawSubcategory[i + 1]);
        if (count > 0) {
          const label = val.includes('$') ? val.split('$')[0] : val;
          const cat = subToCat.get(val) ?? '';
          categoryItems.push({ key: val, label, count, category: cat });
        }
      }
      if (categoryItems.length) result['category'] = categoryItems;
    }

    return result;
  }

  private toIsoDate(val: unknown): string {
    if (!val) return '';
    if (typeof val === 'string') return val;
    return String(val);
  }

  private str(val: unknown): string {
    return val != null ? String(val) : '';
  }

  private firstStr(val: unknown): string {
    if (!val) return '';
    if (Array.isArray(val)) return val.length ? String(val[0]) : '';
    return String(val);
  }

  private strArray(val: unknown): string[] {
    if (!val) return [];
    if (Array.isArray(val)) return val.map(String);
    return [String(val)];
  }

  private escape(input: string): string {
    return input
      .replace(/[+\-!(){}[\]^"~*?:\\/]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
}
